import { useState } from 'react';
import {
  Color, Size, TIME_OFF_MINE_PATH, TIME_OFF_REQUESTS_PATH, emptyTimeOffDraft, fill, formatDateOnly, t, timeOffCancelPath, timeOffDraftProblems, timeOffHoursText, typedHours,
  type DateFormat, type TimeOffDraft, type TimeOffMine, type TimeOffRequestRow,
} from '@bms2/shared';
import { Button, ConfirmationDialog, DatePicker, NumericInput, Select, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import styles from './HrPages.module.css';

/** A request's days in the reader's own date format. */
export function timeOffSpan(row: Pick<TimeOffRequestRow, 'startDate' | 'endDate'>, format: DateFormat): string {
  const first = formatDateOnly(`${row.startDate}T00:00:00.000Z`, format);
  return row.startDate === row.endDate ? first : `${first} – ${formatDateOnly(`${row.endDate}T00:00:00.000Z`, format)}`;
}

/**
 * Request Time Off on My Info (piece 34; decision 13030; T3871): the plan, the
 * first and last day, the hours of a part day and a note, with the plan's
 * balance read from the ledger and shown read-only. 🔒 Drawn through the Form
 * frame; the route counts the hours and refuses a request past the balance, in
 * the past or over another, in a sentence the frame shows.
 */
export function RequestTimeOffWindow({ apiBaseUrl = '', mine, onClose, onSent }: {
  apiBaseUrl?: string;
  mine: TimeOffMine;
  onClose: () => void;
  onSent: () => void;
}) {
  const [draft, setDraft] = useState<TimeOffDraft>(() => emptyTimeOffDraft(mine.plans, mine.today));
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: TimeOffDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const problems = timeOffDraftProblems(draft, mine.today);
  const first = Object.values(problems)[0] ?? '';
  const plan = mine.plans.find((row) => row.planId === draft.planId) ?? null;
  const save = async () => {
    const answer = await post<{ requestId: string }>(TIME_OFF_REQUESTS_PATH, draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSent();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.timeOff.title')}
      dataForm="request-time-off"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.timeOff.act'), onAct: () => save(), disabled: first !== '', disabledReason: first }}
    >
      <div className={styles.fields} data-request-time-off={mine.employmentId ?? ''}>
        <p className={styles.meta}>{t('hr.timeOff.lead')}</p>
        <Select
          size={Size.Medium}
          label={t('hr.timeOff.plan')}
          value={draft.planId}
          onChange={(planId) => change({ ...draft, planId })}
          options={mine.plans.map((row) => ({ value: row.planId, label: row.planName }))}
          placeholder={t('hr.timeOff.choosePlan')}
          dataAttribute={{ 'data-request-time-off-plan': 'true' }}
          required
        />
        {/* 🔒 Decision 13030: the balance is the ledger's, shown and never typed. */}
        {plan ? (
          <>
            <p className={styles.meta} data-request-time-off-balance={plan.planId}>
              {fill(t('hr.timeOff.balanceLine'), { available: timeOffHoursText(plan.available), pending: timeOffHoursText(plan.pending), requestable: timeOffHoursText(Math.max(0, plan.requestable)) })}
            </p>
            {/* 🔒 Decision 13030: no accrual is projected, so the figure is the hours earned to date and the window says so. */}
            <p className={styles.meta} data-request-time-off-hint="true">{t('hr.timeOff.balanceHint')}</p>
          </>
        ) : null}
        <DatePicker size={Size.Medium} label={t('hr.timeOff.firstDay')} value={draft.startDate} min={mine.today} onChange={(startDate) => change({ ...draft, startDate, endDate: draft.endDate < startDate ? startDate : draft.endDate })} error={typed ? (problems.startDate ?? null) : null} required name="timeOffFirstDay" />
        <DatePicker size={Size.Medium} label={t('hr.timeOff.lastDay')} value={draft.endDate} min={draft.startDate || mine.today} onChange={(endDate) => change({ ...draft, endDate })} error={typed ? (problems.endDate ?? null) : null} required name="timeOffLastDay" />
        <NumericInput
          size={Size.Small}
          kind="number"
          decimals={2}
          label={t('hr.timeOff.hours')}
          value={typedHours(draft.hours)}
          onChange={(hours) => change({ ...draft, hours: hours === null ? '' : String(hours) })}
          hint={t('hr.timeOff.hoursHint')}
          error={typed ? (problems.hours ?? null) : null}
          name="timeOffHours"
        />
        <Textarea size={Size.Large} label={t('hr.timeOff.note')} value={draft.note} onChange={(note) => change({ ...draft, note })} error={problems.note ?? null} name="timeOffNote" />
      </div>
    </FormFrame>
  );
}

/**
 * The Time Off section's acts on My Info (decision 13030): Request Time Off,
 * and Cancel Request on each request the person may still cancel. 🔒 Both read
 * the person's own balances and requests afresh, so the window opens on the
 * ledger as it stands.
 */
export function TimeOffControls({ apiBaseUrl, onChanged }: { apiBaseUrl: string; onChanged: () => void }) {
  const format = useDateFormat();
  const read = useApi<TimeOffMine>(TIME_OFF_MINE_PATH, apiBaseUrl);
  const [asking, setAsking] = useState(false);
  const [cancelling, setCancelling] = useState<TimeOffRequestRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  if (read.state.kind !== 'ready') return null;
  // 🔨 A reply missing a list reads as an empty one, so the section never breaks on a short answer.
  const mine: TimeOffMine = { ...read.state.data, plans: read.state.data.plans ?? [], requests: read.state.data.requests ?? [] };
  const changed = (line: string) => {
    setSaid(line);
    read.reload();
    onChanged();
  };
  const cancel = async () => {
    if (!cancelling) return;
    setProblem(null);
    const answer = await post<{ requestId: string }>(timeOffCancelPath(cancelling.id), {}, apiBaseUrl);
    setCancelling(null);
    if (!answer.ok) return setProblem(answer.message);
    changed(t('hr.timeOff.cancelled'));
  };
  const cancellable = mine.requests.filter((row) => row.mayCancel);
  return (
    <div className={styles.fields} data-time-off-controls={mine.employmentId ?? ''}>
      {mine.plans.length === 0 ? (
        <p className={styles.meta} data-time-off-none="true">{t('hr.timeOff.noPlans')}</p>
      ) : (
        <div className={styles.controls}>
          <span data-time-off-request="open">
            <Button size={Size.Small} color={Color.Navy} text={[t('hr.timeOff.request')]} actions={[() => { setSaid(null); setProblem(null); setAsking(true); }]} />
          </span>
        </div>
      )}
      {said ? <p className={styles.meta} role="status" data-time-off-said="true">{said}</p> : null}
      {problem ? <p className={styles.meta} role="alert" data-time-off-problem="true">{problem}</p> : null}
      {cancellable.map((row) => (
        <div key={row.id} className={styles.controls} data-time-off-cancellable={row.id}>
          <span className={styles.meta}>{line(row.planName, '·', timeOffSpan(row, format), '·', timeOffHoursText(row.hours))}</span>
          <span data-time-off-cancel={row.id}>
            <Button size={Size.Small} color={Color.Inactive} text={[t('hr.timeOff.cancel')]} actions={[() => setCancelling(row)]} />
          </span>
        </div>
      ))}
      {/* 🔨 Mounted only while open, so a second opening reads the balance afresh. */}
      {asking ? (
        <RequestTimeOffWindow
          key={`time-off-${mine.employmentId ?? ''}`}
          apiBaseUrl={apiBaseUrl}
          mine={mine}
          onClose={() => setAsking(false)}
          onSent={() => {
            setAsking(false);
            changed(t('hr.timeOff.submitted'));
          }}
        />
      ) : null}
      <ConfirmationDialog
        open={cancelling !== null}
        title={t('hr.timeOff.cancelTitle')}
        consequence={{ body: t('hr.timeOff.cancelConfirm'), recordNames: cancelling ? [line(cancelling.planName, '·', timeOffSpan(cancelling, format))] : [] }}
        confirmLabel={t('hr.timeOff.cancel')}
        onConfirm={() => void cancel()}
        onCancel={() => setCancelling(null)}
      />
    </div>
  );
}
